/**
 * /closing/email-template — "Closing Email Template Editor". Migrated from
 * nuadvisory `ClosingEmailAdmin.cshtml` + `templatePage.js` (TinyMCE + jQuery).
 *
 * Content-only page: the authenticated shell (header/sidebar/footer) is
 * provided by `WebShellLayout` via `<Outlet>`, so this component returns ONLY
 * its page content — no shell wrapper (double-shell otherwise).
 *
 * Frontend-only migration. The .NET API stays in nu/ — this component calls
 * the existing `/api/Templates` endpoints cross-origin.
 *
 * Auth: uses the `nu_assist_session` JWT cookie which the nuadvisory
 * `[Authorize]` policy accepts cross-origin. All fetch calls use
 * `credentials: "include"` — no explicit Authorization header (the SlideCreator
 * pattern; the migrate skill's "Bearer header" pitfall does NOT match the
 * working code).
 *
 * API base URL: VITE_NUADVISORY_API_URL env var.
 */
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useMe } from "@nu-assist/shared-fe";
import { useAdvisoryFetch, type FetchLike } from "../../context/AdvisoryClientContext";
import { NUADVISORY_BASE } from "../../lib/advisory";
import { Button } from "@nu-assist/ui";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { type Placeholder, RichTextEditor, type RichTextEditorHandle } from "./RichTextEditor";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Hand-typed from the .NET `TemplateViewModel`. `id === 0` ⇒ create. */
interface EmailTemplate {
  id: number;
  name: string;
  content: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CREATE_NEW = "createNew";

// Parity with nuadvisory: a brand-new template seeds the greeting line.
const SEED_CONTENT = "<p>{first_name},</p>";

const TEMPLATES_KEY = ["nuadvisory", "templates"] as const;

// `{first_name}` keeps parity with nuadvisory; the rest are the optional
// enhancement set `TemplateHelper.cs` already supports server-side.
const PLACEHOLDERS: Placeholder[] = [
  { label: "First Name", token: "{first_name}" },
  { label: "Last Name", token: "{last_name}" },
  { label: "User Email", token: "{user_email}" },
  { label: "User Name", token: "{user_name}" },
];

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

async function fetchTemplates(fetchImpl: FetchLike = fetch): Promise<EmailTemplate[]> {
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/Templates`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`GET /api/Templates ${res.status}`);
  return res.json() as Promise<EmailTemplate[]>;
}

async function upsertTemplate(template: EmailTemplate, fetchImpl: FetchLike = fetch): Promise<EmailTemplate> {
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/Templates`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(template),
  });
  if (!res.ok) throw new Error(`POST /api/Templates ${res.status}`);
  return res.json() as Promise<EmailTemplate>;
}

async function disableTemplate(id: number, fetchImpl: FetchLike = fetch): Promise<void> {
  const res = await fetchImpl(`${NUADVISORY_BASE}/api/Templates/disable/${id}`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`POST /api/Templates/disable/${id} ${res.status}`);
}

/**
 * True when the HTML carries no visible text (mirrors nu's empty check).
 *
 * Parses the HTML and reads `textContent` so ALL entities decode — named
 * (`&nbsp;`) and numeric (`&#160;`) alike — then treats a non-breaking space
 * as whitespace. A naive tag-strip + `&nbsp;`-only replace would miss numeric
 * entities and wrongly accept a blank-looking template. `DOMParser` builds an
 * inert document (no script execution, no resource loading). Exported for unit
 * tests.
 */
export function isContentEmpty(html: string): boolean {
  const text = new DOMParser().parseFromString(html, "text/html").body.textContent ?? "";
  return text.trim().length === 0;
}

type ConfirmAction = "save" | "disable" | null;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EmailTemplateEditor() {
  const me = useMe();
  const queryClient = useQueryClient();
  const advisoryFetch = useAdvisoryFetch();
  const editorRef = useRef<RichTextEditorHandle>(null);

  const [selectedId, setSelectedId] = useState(0); // 0 = new
  const [name, setName] = useState("");
  const [content, setContent] = useState(SEED_CONTENT);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

  const templatesQuery = useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: () => fetchTemplates(advisoryFetch),
  });

  const resetFields = () => {
    setSelectedId(0);
    setName("");
    setContent(SEED_CONTENT);
    editorRef.current?.setContent(SEED_CONTENT);
  };

  const upsertMutation = useMutation({
    mutationFn: (template: EmailTemplate) => upsertTemplate(template, advisoryFetch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
      toast.success("Template saved successfully.");
      resetFields();
      setConfirmAction(null);
    },
    onError: () => {
      toast.error("Failed to save the template. Please try again.");
      setConfirmAction(null);
    },
  });

  const disableMutation = useMutation({
    mutationFn: (id: number) => disableTemplate(id, advisoryFetch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
      toast.success("Template disabled successfully.");
      resetFields();
      setConfirmAction(null);
    },
    onError: () => {
      toast.error("Failed to disable the template. Please try again.");
      setConfirmAction(null);
    },
  });

  const handleSelectChange = (value: string) => {
    if (value === CREATE_NEW) {
      resetFields();
      return;
    }
    const template = templatesQuery.data?.find((t) => String(t.id) === value);
    if (!template) return;
    setSelectedId(template.id);
    setName(template.name);
    setContent(template.content);
    editorRef.current?.setContent(template.content);
  };

  const handleSaveClick = () => {
    if (!name.trim() || isContentEmpty(content)) {
      toast.warning("Please provide a name and content for the template.");
      return;
    }
    setConfirmAction("save");
  };

  const handleDeleteClick = () => {
    if (selectedId === 0) {
      toast.warning("Please select a template to delete.");
      return;
    }
    setConfirmAction("disable");
  };

  const handleConfirm = () => {
    if (confirmAction === "save") {
      upsertMutation.mutate({ id: selectedId, name: name.trim(), content });
    } else if (confirmAction === "disable") {
      disableMutation.mutate(selectedId);
    }
  };

  // Defensive narrow — WebShellLayout's AuthGuard already wraps this.
  if (me.status !== "authenticated") return null;

  return (
    <div data-testid="email-template-editor" className="p-12">
      <div className="mx-auto max-w-3xl">
        <h1 className="mb-6 text-h1 text-text">Closing Email Template Editor</h1>

        <div className="flex flex-col gap-4">
          {/* Template picker */}
          <div className="flex flex-col gap-1">
            <label htmlFor="template-select" className="text-label font-medium text-text">
              Select Template
            </label>
            <select
              id="template-select"
              value={selectedId === 0 ? CREATE_NEW : String(selectedId)}
              onChange={(e) => handleSelectChange(e.target.value)}
              className="rounded-sm border border-border bg-surface px-2 py-2 text-body text-text focus:outline-none focus:ring-2 focus:ring-accent"
            >
              <option value={CREATE_NEW}>Create new template</option>
              {(templatesQuery.data ?? []).map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}
                </option>
              ))}
            </select>
            {templatesQuery.isLoading && (
              <span className="text-caption text-text-muted">Loading templates…</span>
            )}
            {templatesQuery.isError && (
              <span className="text-caption text-error">
                Failed to load templates. Please refresh.
              </span>
            )}
          </div>

          {/* Name */}
          <div className="flex flex-col gap-1">
            <label htmlFor="template-name" className="text-label font-medium text-text">
              Name
            </label>
            <input
              id="template-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="A name for this template"
              className="rounded-sm border border-border bg-surface px-3 py-2 text-body text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </div>

          {/* Content */}
          <div className="flex flex-col gap-1">
            <span className="text-label font-medium text-text">Template Content</span>
            <RichTextEditor
              ref={editorRef}
              initialContent={SEED_CONTENT}
              onChange={setContent}
              placeholders={PLACEHOLDERS}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button variant="secondary" type="button" onClick={handleDeleteClick}>
              Delete Template
            </Button>
            <Button type="button" onClick={handleSaveClick}>
              Save Template
            </Button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmAction === "save"}
        title="Confirm Save"
        message="Are you sure you want to save this template?"
        confirmLabel="Save"
        busy={upsertMutation.isPending}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmAction(null)}
      />
      <ConfirmDialog
        open={confirmAction === "disable"}
        title="Confirm Removal"
        message="Are you sure you want to remove this template?"
        confirmLabel="Delete"
        destructive
        busy={disableMutation.isPending}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}
